import { inject, Injectable } from '@angular/core';
import { DataAccessGameService } from '@fantaz/shared/data-access/api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { getGamesLoaded, isAllLoaded } from './games.selectors';

import { Store } from '@ngrx/store';
import * as GamesActions from './games.actions';

@Injectable()
export class GamesEffects {
  gameService = inject(DataAccessGameService);

  init$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GamesActions.initGames),
        concatLatestFrom(() => this.store.select(getGamesLoaded)),
        filter(([, loaded]) => !loaded),
        switchMap(([{ params }]) => {
          return this.gameService.fetchGameList(params).pipe(
            tap(({ items }) => {
              for (const item of items) {
                localStorage.setItem(
                  `game-details-${item.key}`,
                  JSON.stringify(item),
                );
              }
            }),
            map((data) => {
              return GamesActions.loadGamesSuccess({ games: data });
            }),
            catchError((error) => {
              return of(GamesActions.loadGamesFailure({ error }));
            }),
          );
        }),
      ),
    { dispatch: true },
  );

  fetchMoreGames$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GamesActions.loadMoreGames),
        concatLatestFrom(() => this.store.select(isAllLoaded)),
        filter(([, allLoaded]) => !allLoaded),
        switchMap(([{ params }]) => {
          return this.gameService.fetchGameList(params).pipe(
            map((data) => {
              return GamesActions.loadMoreGamesSuccess({ games: data });
            }),
            catchError((error) => {
              return of(GamesActions.loadMoreGamesFailure({ error }));
            }),
          );
        }),
      ),
    { dispatch: true },
  );

  filterGamesByTags$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GamesActions.filterGamesByTags),
        switchMap(({ params }) => {
          return this.gameService.fetchGameList(params).pipe(
            map((data) => {
              return GamesActions.filterGamesByTagsSuccess({ games: data });
            }),
            catchError((error) => {
              return of(GamesActions.filterGamesByTagsFailure({ error }));
            }),
          );
        }),
      ),
    { dispatch: true },
  );

  searchGames$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GamesActions.searchGames),
        debounceTime(400),
        distinctUntilChanged(),
        switchMap(({ params }) => {
          return this.gameService.fetchGameList(params).pipe(
            map((data) => {
              return GamesActions.searchGamesSuccess({ games: data });
            }),
            catchError((error) => {
              return of(GamesActions.searchGamesFailure({ error }));
            }),
          );
        }),
      ),
    { dispatch: true },
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
  ) {}
}
